import { Redirect } from 'expo-router';
import * as React from 'react';
import {
    BanIcon,
    type IconType,
    InfoIcon,
    Mailbox,
    RotateCcwIcon,
    ScanIcon,
} from 'smoothie';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Emphasized,
    Icon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../../../common/providers/AuthedUserProvider';
import { captureException } from '../../../../../logger/Logger';

interface CardSentProps {
    lostOrStolen: boolean;
    onPress: () => void;
}

const Bullet = ({ icon, text }: { icon: IconType; text: string }) => {
    return (
        <XStack gap={12}>
            <Icon
                style={{ marginTop: 4 }}
                icon={icon}
                size={20}
                color="$black"
            />
            <View flex={1}>
                <BodyMediumRegular>{text}</BodyMediumRegular>
            </View>
        </XStack>
    );
};

const CardSent = ({ lostOrStolen, onPress }: CardSentProps) => {
    const user = useAuthedUser();

    const { userAddress } = user;

    if (userAddress == null) {
        captureException(
            new Error('Address not found for user attempting to reissue card'),
            { user },
        );
        // TODO: Maybe show a toast notification to the user encouraging them to contact HD?
        return <Redirect href="/home/banking" />;
    }

    return (
        <YStack flex={1} justifyContent="space-between">
            <YStack gap={6} alignItems="center">
                <View
                    backgroundColor="$green900"
                    borderRadius={20}
                    padding={20}
                    height={90}
                    width={90}
                    alignItems="center"
                    justifyContent="center"
                    marginBottom={32}
                >
                    <Icon icon={Mailbox} size={40} color="$white" />
                </View>
                <H2Emphasized textAlign="center" marginBottom={8}>
                    {lostOrStolen
                        ? 'Your card has been cancelled, and a new one is on the way'
                        : 'Your new card is on the way'}
                </H2Emphasized>
                <BodyMediumRegular
                    textAlign="center"
                    color="$gray1000"
                    marginBottom={32}
                >
                    You should get it in 5-7 business days.
                </BodyMediumRegular>

                <YStack width="100%" gap={12}>
                    {lostOrStolen ? (
                        <Bullet
                            icon={RotateCcwIcon}
                            text="Switch any scheduled payments from the cancelled card to another payment method."
                        />
                    ) : (
                        <Bullet
                            icon={BanIcon}
                            text="Your current card will work until you activate your new card."
                        />
                    )}
                    <Bullet
                        icon={InfoIcon}
                        text="Your PIN will remain the same but can be changed at any time."
                    />
                    {lostOrStolen ? (
                        <Bullet
                            icon={ScanIcon}
                            text="Monitor your transactions and report any unrecognized activity."
                        />
                    ) : (
                        <></>
                    )}
                </YStack>
            </YStack>
            <View gap={12}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    onPress={onPress}
                    size={ButtonSize.LARGE}
                    text="Done"
                />
            </View>
        </YStack>
    );
};

export default CardSent;
